import {
	useGetSenderIdentity,
	useListSenderIdentities,
} from "@package/api-client/hooks/senderIdentitiesController";
import type { CreateSenderIdentityMutationRequestChannelEnum } from "@package/api-client/types/senderIdentitiesController";
import { createFileRoute } from "@tanstack/react-router";
import {
	Check,
	ChevronDown,
	ChevronRight,
	Copy,
	Plus,
	RefreshCw,
	ShieldCheck,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useCreateSenderIdentity,
	useDeleteSenderIdentity,
	useVerifySenderIdentity,
} from "@/api/apps";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/app/$appId/sender-identities/")({
	component: SenderIdentitiesPage,
});

const CHANNEL_PROVIDERS: Record<string, { value: string; label: string }[]> = {
	EMAIL: [{ value: "ses", label: "Amazon SES" }],
	SMS: [{ value: "sns", label: "Amazon SNS" }],
	WHATSAPP: [],
};

const CHANNEL_PLACEHOLDER: Record<string, string> = {
	EMAIL: "example.com",
	SMS: "+1234567890",
	WHATSAPP: "+1234567890",
};

const STATUS_BADGE: Record<string, string> = {
	PENDING: "badge-warning",
	SUCCESS: "badge-success",
	FAILED: "badge-error",
	TEMPORARY_FAILURE: "badge-warning",
};

const STATUS_LABEL: Record<string, string> = {
	PENDING: "senderIdentities.statusPending",
	SUCCESS: "senderIdentities.statusSuccess",
	FAILED: "senderIdentities.statusFailed",
	TEMPORARY_FAILURE: "senderIdentities.statusTemporaryFailure",
};

function DnsRecordsPanel({
	orgId,
	appId,
	identityId,
}: {
	orgId: string;
	appId: string;
	identityId: string;
}) {
	const { t } = useTranslation();
	const { data: identity, isLoading } = useGetSenderIdentity(orgId, appId, identityId);
	const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

	const handleCopy = async (value: string, idx: number) => {
		await navigator.clipboard.writeText(value);
		setCopiedIdx(idx);
		setTimeout(() => setCopiedIdx(null), 2000);
	};

	if (isLoading) {
		return (
			<div className="flex justify-center py-4">
				<span className="loading loading-spinner loading-sm" />
			</div>
		);
	}

	if (!identity?.dnsRecords?.length) {
		return <p className="text-sm text-base-content/50 py-2">{t("common.noResults")}</p>;
	}

	return (
		<div className="bg-base-200 rounded-lg p-4">
			<p className="text-sm text-base-content/70 mb-3">
				{t("senderIdentities.addDnsInstructions")}
			</p>
			<div className="overflow-x-auto">
				<table className="table table-sm">
					<thead>
						<tr>
							<th>{t("senderIdentities.dnsType")}</th>
							<th>{t("senderIdentities.dnsName")}</th>
							<th>{t("senderIdentities.dnsValue")}</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{identity.dnsRecords.map((record, idx) => (
							<tr key={`${record.name}-${record.type}`}>
								<td>
									<code className="text-xs bg-base-300 px-1.5 py-0.5 rounded">{record.type}</code>
								</td>
								<td>
									<code className="text-xs break-all">{record.name}</code>
								</td>
								<td>
									<code className="text-xs break-all">{record.value}</code>
								</td>
								<td>
									<button
										type="button"
										className="btn btn-ghost btn-xs"
										onClick={() => handleCopy(record.value, idx)}
									>
										{copiedIdx === idx ? (
											<Check className="w-3 h-3 text-success" />
										) : (
											<Copy className="w-3 h-3" />
										)}
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function SenderIdentitiesPage() {
	const { t } = useTranslation();
	const { orgId, appId } = Route.useParams();

	const { data: identities, isLoading } = useListSenderIdentities(orgId, appId);

	const createIdentity = useCreateSenderIdentity(orgId, appId);
	const verifyIdentity = useVerifySenderIdentity(orgId, appId);
	const deleteIdentity = useDeleteSenderIdentity(orgId, appId);

	const [showCreateForm, setShowCreateForm] = useState(false);
	const [expandedIdentityId, setExpandedIdentityId] = useState<string | null>(null);
	const [deleteIdentityId, setDeleteIdentityId] = useState<string | null>(null);

	// Create form state
	const [channel, setChannel] = useState("EMAIL");
	const [provider, setProvider] = useState("ses");
	const [identifier, setIdentifier] = useState("");

	const providers = CHANNEL_PROVIDERS[channel] ?? [];

	const handleChannelChange = (newChannel: string) => {
		setChannel(newChannel);
		const newProviders = CHANNEL_PROVIDERS[newChannel] ?? [];
		setProvider(newProviders[0]?.value ?? "");
		setIdentifier("");
	};

	const resetForm = () => {
		setChannel("EMAIL");
		setProvider("ses");
		setIdentifier("");
	};

	const handleCreate = (e: React.FormEvent) => {
		e.preventDefault();
		if (!identifier.trim()) return;
		createIdentity.mutate(
			{
				orgId,
				appId,
				data: {
					// `channel` is constrained to the enum values by the form's CHANNEL_PROVIDERS map.
					channel: channel as CreateSenderIdentityMutationRequestChannelEnum,
					provider,
					identifier: identifier.trim(),
				},
			},
			{
				onSuccess: (data) => {
					resetForm();
					setShowCreateForm(false);
					setExpandedIdentityId(data.id);
				},
			},
		);
	};

	const handleVerify = (identityId: string) => {
		verifyIdentity.mutate({ orgId, appId, identityId });
	};

	const handleDelete = (identityId: string) => {
		deleteIdentity.mutate(
			{ orgId, appId, identityId },
			{ onSuccess: () => setDeleteIdentityId(null) },
		);
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<h1 data-testid="sender-identities-title" className="text-2xl font-bold">
					{t("senderIdentities.title")}
				</h1>
				<button
					type="button"
					data-testid="sender-identities-create-btn"
					className="btn btn-primary btn-sm"
					onClick={() => setShowCreateForm(true)}
				>
					<Plus className="w-4 h-4" />
					{t("senderIdentities.createNew")}
				</button>
			</div>

			{/* Identities Table */}
			<div className="card bg-base-100 border border-base-300 shadow-sm">
				<div className="card-body">
					{isLoading && (
						<div className="flex justify-center py-8">
							<span className="loading loading-spinner loading-md" />
						</div>
					)}

					{!isLoading && (!identities || identities.length === 0) && (
						<div className="text-center py-8 text-base-content/50">
							<ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
							<p>{t("senderIdentities.noIdentities")}</p>
							<p className="text-sm mt-1">{t("senderIdentities.createFirst")}</p>
						</div>
					)}

					{!isLoading && identities && identities.length > 0 && (
						<div className="overflow-x-auto">
							<table data-testid="sender-identities-table" className="table">
								<thead>
									<tr>
										<th />
										<th>{t("senderIdentities.identifier")}</th>
										<th>{t("senderIdentities.channel")}</th>
										<th>{t("senderIdentities.provider")}</th>
										<th>{t("senderIdentities.status")}</th>
										<th>{t("messages.createdAt")}</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{identities.map((identity) => (
										<>
											<tr key={identity.id}>
												<td>
													<button
														type="button"
														className="btn btn-ghost btn-xs btn-square"
														onClick={() =>
															setExpandedIdentityId(
																expandedIdentityId === identity.id ? null : identity.id,
															)
														}
													>
														{expandedIdentityId === identity.id ? (
															<ChevronDown className="w-3.5 h-3.5" />
														) : (
															<ChevronRight className="w-3.5 h-3.5" />
														)}
													</button>
												</td>
												<td className="font-medium">{identity.identifier}</td>
												<td>
													<span className="badge badge-sm badge-ghost">{identity.channel}</span>
												</td>
												<td className="text-sm text-base-content/60">{identity.provider}</td>
												<td>
													<span
														className={`badge badge-sm ${STATUS_BADGE[identity.status] ?? "badge-ghost"}`}
													>
														{t(STATUS_LABEL[identity.status] ?? "senderIdentities.statusPending")}
													</span>
												</td>
												<td className="text-sm text-base-content/60">
													{new Date(identity.createdAt).toLocaleDateString()}
												</td>
												<td>
													<div className="flex gap-1 justify-end">
														<button
															type="button"
															className="btn btn-ghost btn-xs"
															disabled={verifyIdentity.isPending}
															onClick={() => handleVerify(identity.id)}
														>
															<RefreshCw
																className={`w-3 h-3 ${verifyIdentity.isPending ? "animate-spin" : ""}`}
															/>
															{verifyIdentity.isPending
																? t("senderIdentities.verifying")
																: t("senderIdentities.verifyNow")}
														</button>
														<button
															type="button"
															className="btn btn-ghost btn-xs text-error"
															onClick={() => setDeleteIdentityId(identity.id)}
														>
															<Trash2 className="w-3 h-3" />
														</button>
													</div>
												</td>
											</tr>
											{expandedIdentityId === identity.id && (
												<tr key={`${identity.id}-dns`}>
													<td colSpan={7} className="p-0 px-4 pb-4">
														<DnsRecordsPanel orgId={orgId} appId={appId} identityId={identity.id} />
													</td>
												</tr>
											)}
										</>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>

			{/* Create Modal */}
			{showCreateForm && (
				<dialog className="modal modal-open">
					<div className="modal-box">
						<h3 className="font-bold text-lg">{t("senderIdentities.createNew")}</h3>
						<form onSubmit={handleCreate} className="mt-4 space-y-4">
							<div className="form-control">
								<label className="label" htmlFor="si-channel">
									<span className="label-text">{t("senderIdentities.channel")}</span>
								</label>
								<select
									id="si-channel"
									className="select select-bordered w-full"
									value={channel}
									onChange={(e) => handleChannelChange(e.target.value)}
								>
									<option value="EMAIL">EMAIL</option>
									<option value="SMS">SMS</option>
									<option value="WHATSAPP">WHATSAPP</option>
								</select>
							</div>
							<div className="form-control">
								<label className="label" htmlFor="si-provider">
									<span className="label-text">{t("senderIdentities.provider")}</span>
								</label>
								{providers.length > 0 ? (
									<select
										id="si-provider"
										className="select select-bordered w-full"
										value={provider}
										onChange={(e) => setProvider(e.target.value)}
									>
										{providers.map((p) => (
											<option key={p.value} value={p.value}>
												{p.label}
											</option>
										))}
									</select>
								) : (
									<div className="text-sm text-base-content/50 bg-base-200 rounded-lg px-4 py-3">
										{t("senderIdentities.noProviders")}
									</div>
								)}
							</div>
							{providers.length > 0 && (
								<div className="form-control">
									<label className="label" htmlFor="si-identifier">
										<span className="label-text">{t("senderIdentities.identifier")}</span>
									</label>
									<input
										id="si-identifier"
										type="text"
										data-testid="si-identifier-input"
										className="input input-bordered w-full"
										placeholder={CHANNEL_PLACEHOLDER[channel] ?? ""}
										value={identifier}
										onChange={(e) => setIdentifier(e.target.value)}
										autoFocus
									/>
								</div>
							)}
							<div className="modal-action">
								<button
									type="button"
									className="btn"
									onClick={() => {
										setShowCreateForm(false);
										resetForm();
									}}
								>
									{t("common.cancel")}
								</button>
								<button
									type="submit"
									className="btn btn-primary"
									disabled={!identifier.trim() || !provider || createIdentity.isPending}
								>
									{createIdentity.isPending ? t("common.creating") : t("common.create")}
								</button>
							</div>
						</form>
					</div>
					<form method="dialog" className="modal-backdrop">
						<button
							type="button"
							onClick={() => {
								setShowCreateForm(false);
								resetForm();
							}}
						>
							close
						</button>
					</form>
				</dialog>
			)}

			{/* Delete Confirmation Modal */}
			{deleteIdentityId && (
				<dialog className="modal modal-open">
					<div className="modal-box">
						<h3 className="font-bold text-lg">{t("common.confirm")}</h3>
						<p className="py-4">{t("senderIdentities.deleteConfirm")}</p>
						<div className="modal-action">
							<button type="button" className="btn" onClick={() => setDeleteIdentityId(null)}>
								{t("common.cancel")}
							</button>
							<button
								type="button"
								className="btn btn-error"
								disabled={deleteIdentity.isPending}
								onClick={() => handleDelete(deleteIdentityId)}
							>
								{deleteIdentity.isPending ? t("common.deleting") : t("common.delete")}
							</button>
						</div>
					</div>
					<form method="dialog" className="modal-backdrop">
						<button type="button" onClick={() => setDeleteIdentityId(null)}>
							close
						</button>
					</form>
				</dialog>
			)}
		</div>
	);
}
